import { useMediaQuery, useTheme } from '@mui/material';

/**
 * Spike 1 — mobile shell breakpoint classification (post-lock 2026-05-25 Mode
 * Commitment Doc; spike plan §1.3).
 *
 * The shell paradigm switches at MUI's `md` (900px):
 *
 *   xs / sm  (< 900px)  → mobile shell:  bottom-tab nav + drawer overlays.
 *   md       (< 1200px) → desktop shell with collapsed rails (rails expand on click).
 *   lg+      (≥ 1200px) → desktop shell with fixed side rails (current behavior).
 *
 * The desktop shell continues to be the shell most consumers see; the mobile
 * shell is a v3.0 ship-criterion (per UI spec §20) but explicitly handles
 * the lower-precision-pointer subset of operations (consumption + light edit).
 * Heavy creation modes show the deferral overlay regardless of viewport when
 * the user lands on them via a mobile shell.
 *
 * Hook returns a small bag so consumers can switch on the most expressive
 * dimension without re-querying media each render. Use `isMobileShell` for
 * the top-level shell switch in `App.tsx`; use `compactRails` if you want to
 * collapse a rail at md without flipping the whole shell.
 */
export interface MobileShellState {
  /** True for xs + sm (< 900px). The bottom-tab shell mounts. */
  isMobileShell: boolean;
  /**
   * True for md (900-1199px). The desktop shell mounts but rails should
   * default to collapsed (icon-only, click-to-expand-as-overlay).
   */
  compactRails: boolean;
  /** True for lg+ (≥ 1200px). The desktop shell with fixed side rails. */
  isWideDesktop: boolean;
  /** A coarse classifier useful for analytics / debugging. */
  viewport: 'phone' | 'tablet-portrait' | 'tablet-landscape' | 'desktop';
}

export function useMobileShell(): MobileShellState {
  const theme = useTheme();
  // MUI's `down(key)` is `< theme.breakpoints.values[key]` — so down('md') is < 900px.
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));            // < 600px
  const isTabletPortrait = useMediaQuery(theme.breakpoints.between('sm', 'md')); // 600-899px
  const isTabletLandscape = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900-1199px
  const isWide = useMediaQuery(theme.breakpoints.up('lg'));               // ≥ 1200px

  const isMobileShell = isPhone || isTabletPortrait;
  const viewport: MobileShellState['viewport'] = isPhone
    ? 'phone'
    : isTabletPortrait
      ? 'tablet-portrait'
      : isTabletLandscape
        ? 'tablet-landscape'
        : 'desktop';

  return {
    isMobileShell,
    compactRails: isTabletLandscape,
    isWideDesktop: isWide,
    viewport,
  };
}
